import React, { useState } from 'react';
import { Check, ChevronDown, ChevronRight, ExternalLink, PenTool, RefreshCw, User } from 'lucide-react';

import { formatIST } from '@/lib/utils';
import { PRIORITY_MAP } from '@/lib/status-colors';
import { BottomSheet, Card, ChipRow, EmptyState, PageHeader, PhonePage, PillButton, ScreenLoader, SearchBar, StatusPill } from '@/components/phone/primitives';
import { Colors, priorityTone } from '@/components/phone/theme';

/**
 * The design queue, on a phone.
 *
 * The desktop is a grid beside a rail of designers; at a handset's width the
 * rail folds under the grid and the grid scrolls sideways. Here each order is a
 * card that says what is being made, for whom and who has it, and the Designer
 * line is still the control — it opens a sheet at the thumb with the same names
 * and the same workload numbers. The designers themselves are a card at the
 * foot of the list.
 */

const DESIGN_STATUS = {
  pending_assignment: ['Pending', 'neutral'],
  assigned: ['Assigned', 'accent'],
  in_progress: ['In progress', 'caution'],
  completed: ['Done', 'positive'],
  not_required: ['N/A', 'neutral'],
};

const isItemOpen = (item) =>
  item.design_status !== 'completed' && item.design_status !== 'not_required';

const orderDesigner = (order) => order.computed_designer_name || order.assigned_designer_name || null;

/** Who has it, and — for those who may hand it out — a sheet to change that. */
function DesignerLine({ order, item, designers, busyId, currentUserId, canAssign, onAssign }) {
  const [open, setOpen] = useState(false);
  const current = item ? item.assigned_designer_name : orderDesigner(order);
  const currentId = item ? item.assigned_designer_id : order.assigned_designer_id;
  const multiple = !item && order.has_multiple_designers;
  const key = item ? item.id : order.id;
  const label = current ? `${current}${multiple ? ' +' : ''}` : 'Unassigned';

  if (!canAssign) {
    return (
      <span className="text-[13px] font-medium" style={{ color: current ? Colors.text.primary : Colors.semantic.caution }}>
        {label}
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        disabled={busyId === key}
        data-testid={item ? `assign-item-${item.id}` : `assign-btn-${order.order_id}`}
        onClick={() => setOpen(true)}
        className="flex h-10 max-w-full items-center gap-1.5 rounded-[10px] border px-3 text-[13px] font-semibold disabled:opacity-50"
        style={{
          backgroundColor: Colors.background.primary,
          borderColor: Colors.border.default,
          color: current ? Colors.text.primary : Colors.semantic.caution,
        }}
      >
        <PenTool className="h-3.5 w-3.5 shrink-0" style={{ color: Colors.text.tertiary }} />
        <span className="truncate">{label}</span>
        <ChevronDown className="h-4 w-4 shrink-0" style={{ color: Colors.text.secondary }} />
      </button>

      <BottomSheet
        open={open}
        onClose={() => setOpen(false)}
        title={item ? 'Assign this item' : `Assign all ${order.line_items?.length || 0} items`}
        subtitle={item ? item.item_name : order.order_id}
        testID={`designer-sheet-${key}`}
      >
        <div className="max-h-[21rem] overflow-y-auto">
          {designers.map(d => {
            const on = d.id === currentId;
            return (
              <button
                key={d.id}
                type="button"
                data-testid={`pick-designer-${d.id}`}
                onClick={() => { setOpen(false); onAssign(order, item, d.id); }}
                className="flex w-full items-center justify-between gap-3 border-b px-1 py-3.5 text-left last:border-b-0"
                style={{ borderColor: Colors.border.subtle }}
              >
                <span className="flex min-w-0 items-center gap-2">
                  {on && <Check className="h-[18px] w-[18px] shrink-0" style={{ color: Colors.accent.DEFAULT }} />}
                  <span className="truncate text-[15px]" style={{ color: on ? Colors.accent.DEFAULT : Colors.text.primary, fontWeight: on ? 700 : 400 }}>
                    {d.full_name}
                  </span>
                </span>
                <span className="shrink-0 text-[12px] tabular-nums" style={{ color: Colors.text.tertiary }}>
                  {d.current_workload || 0} orders · {d.item_workload || 0} items
                </span>
              </button>
            );
          })}
          {currentUserId && (
            <button
              type="button"
              data-testid="assign-to-me"
              onClick={() => { setOpen(false); onAssign(order, item, currentUserId); }}
              className="mt-1 flex w-full items-center gap-2 border-t px-1 py-3.5 text-left text-[15px] font-semibold"
              style={{ borderColor: Colors.border.default, color: Colors.accent.DEFAULT }}
            >
              <User className="h-4 w-4" /> Assign to me
            </button>
          )}
        </div>
      </BottomSheet>
    </>
  );
}

function OrderCard({ order, expanded, onToggle, onOpen, designerProps }) {
  const items = order.line_items || [];
  const done = items.filter(i => !isItemOpen(i)).length;
  const title = items[0]?.item_name || order.customer_name;

  return (
    <Card className="mt-3" testID={`design-order-${order.order_id}`}>
      <div className="flex items-start justify-between gap-2">
        <button type="button" onClick={onOpen} className="min-w-0 flex-1 text-left" data-testid={`view-order-${order.order_id}`}>
          <span className="line-clamp-2 block text-[15px] font-bold -tracking-[0.2px]" style={{ color: Colors.text.primary }}>
            {title}{items.length > 1 && <span className="font-medium" style={{ color: Colors.text.tertiary }}> +{items.length - 1} more</span>}
          </span>
          <span className="mt-0.5 block truncate text-[12px] font-medium" style={{ color: Colors.text.secondary }}>
            {order.customer_name}{order.firm_name ? ` — ${order.firm_name}` : ''}
          </span>
        </button>
        <ExternalLink className="mt-1 h-4 w-4 shrink-0" style={{ color: Colors.text.tertiary }} />
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]" style={{ color: Colors.text.secondary }}>
        <span className="font-medium tabular-nums">{order.order_id}</span>
        {!!order.priority && (
          <StatusPill label={PRIORITY_MAP[order.priority]?.label || order.priority} tone={priorityTone(order.priority)} />
        )}
        <span>Due {order.expected_delivery_date ? formatIST(order.expected_delivery_date, { dateOnly: true }) : '—'}</span>
        {items.length > 0 && <span className="tabular-nums">{done}/{items.length} designed</span>}
      </div>

      <div className="mt-3 flex items-center justify-between gap-2">
        <DesignerLine order={order} {...designerProps} />
        {items.length > 0 && (
          <button
            type="button"
            onClick={onToggle}
            aria-label={expanded ? 'Collapse items' : 'Expand items'}
            data-testid={`expand-order-${order.order_id}`}
            className="flex h-10 shrink-0 items-center gap-1 rounded-[10px] px-2 text-[12px] font-semibold"
            style={{ color: Colors.accent.DEFAULT }}
          >
            {items.length} item{items.length === 1 ? '' : 's'}
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        )}
      </div>

      {expanded && (
        <ul className="mt-2 border-t" style={{ borderColor: Colors.border.subtle }}>
          {items.map(item => {
            const [label, tone] = DESIGN_STATUS[item.design_status] || DESIGN_STATUS.pending_assignment;
            return (
              <li
                key={item.id}
                className="flex items-center justify-between gap-2 border-b py-2.5 last:border-b-0"
                style={{ borderColor: Colors.border.subtle }}
                data-testid={`design-item-${item.id}`}
              >
                <div className="min-w-0">
                  <p className="truncate text-[14px] font-medium" style={{ color: Colors.text.primary }}>
                    {item.item_name || item.product_subtype || item.order_type}
                  </p>
                  <p className="mt-0.5 flex items-center gap-1.5 text-[12px]" style={{ color: Colors.text.secondary }}>
                    <StatusPill label={label} tone={tone} /> Qty {item.quantity?.toLocaleString()}
                  </p>
                </div>
                {isItemOpen(item)
                  ? <DesignerLine order={order} item={item} {...designerProps} />
                  : <span className="text-[12px]" style={{ color: Colors.text.tertiary }}>—</span>}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

export default function PhoneDesignQueue({
  orders, stats, totalInQueue, designers, filter, setFilter, loading, setSearchQuery,
  expanded, toggleExpanded, busyId, canAssign, currentUserId, onAssign, onRefresh, onOpenOrder,
}) {
  const designerProps = { designers, busyId, currentUserId, canAssign, onAssign };
  const chips = [
    { key: null, label: `All · ${totalInQueue}` },
    { key: 'unassigned', label: `Unassigned · ${stats.unassigned || 0}` },
    { key: 'assigned', label: `Assigned · ${stats.in_progress || 0}` },
  ];

  return (
    <PhonePage testID="design-queue-page">
      <PageHeader
        title="Design Queue"
        subtitle={`${totalInQueue} in queue`}
        action={<PillButton onClick={onRefresh} testID="refresh-btn"><RefreshCw className="h-4 w-4" /> Refresh</PillButton>}
      />

      <div className="flex-1 px-4 pb-10">
        <SearchBar placeholder="Order ID, voice number, customer…" onChange={setSearchQuery} />
        <div className="mt-3">
          <ChipRow chips={chips} active={filter} onSelect={setFilter} testIDPrefix="chip" />
        </div>

        {loading ? <ScreenLoader /> : orders.length === 0 ? (
          <EmptyState icon={PenTool} message="No orders in design queue" />
        ) : orders.map(order => (
          <OrderCard
            key={order.id}
            order={order}
            expanded={expanded.has(order.id)}
            onToggle={() => toggleExpanded(order.id)}
            onOpen={() => onOpenOrder(order)}
            designerProps={designerProps}
          />
        ))}

        {canAssign && (
          <Card className="mt-5" title="Designers" aside={`${designers.length} active`} testID="designers-card">
            {designers.length === 0 ? (
              <p className="py-2 text-center text-[13px]" style={{ color: Colors.text.tertiary }}>No designers found</p>
            ) : designers.map(d => {
              const load = d.current_workload || 0;
              return (
                <div
                  key={d.id}
                  className="flex items-center justify-between gap-2 border-b py-2.5 last:border-b-0"
                  style={{ borderColor: Colors.border.subtle }}
                  data-testid={`designer-card-${d.id}`}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-[14px] font-medium" style={{ color: Colors.text.primary }}>{d.full_name}</span>
                    <span className="block text-[11px] capitalize" style={{ color: Colors.text.tertiary }}>{d.role?.replace('_', ' ')}</span>
                  </span>
                  <span className="shrink-0 text-[12px] tabular-nums" style={{ color: load > 3 ? Colors.semantic.critical : Colors.text.secondary }}>
                    {load} orders{d.item_workload > 0 ? ` · ${d.item_workload} items` : ''}
                  </span>
                </div>
              );
            })}
          </Card>
        )}
      </div>
    </PhonePage>
  );
}
